import { Box, FormControl, MenuItem, Select, Stack, Tooltip, Typography } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SwitchBtn from "../../components/Buttons/switchBtn";
import { numberFormatter } from "../../helper/Reports/common";
import StickyInsightsTable from "../../components/Tables/StickyInsightsTable";

function normalizeStatus(status) {
  return String(status ?? "").toUpperCase();
}

function isColumnSortable(column) {
  return !!column && column.type === "number";
}

function getAlign(column) {
  return column?.alignment || (column?.type === "number" ? "right" : "left");
}

export default function CampaignListV2Table({
  columns = [],
  rows = [],
  isLoading = false,
  queryOrder = [["spends", "desc"]],
  summaryData = {},
  onSortClick,
  onToggleRow,
  onToggleAll,
  onToggleStatus,
  onChangeBidStrategy,
  noDataFoundMessage = "No Data Found",
}) {
  const renderStringCell = (column, row) => {
    const value = row?.[column.key];
    if (value === undefined || value === null || value === "") return "-";
    const isTargetingCol = column.key === "targeting";
    return (
      <Tooltip title={String(value)} arrow placement="top">
        <Typography
          sx={{
            fontSize: "12px",
            color: "#333",
            overflow: isTargetingCol ? "visible" : "hidden",
            textOverflow: isTargetingCol ? "unset" : "ellipsis",
            whiteSpace: isTargetingCol ? "normal" : "nowrap",
            wordBreak: isTargetingCol ? "break-word" : "normal",
            maxWidth: column.minWidth || 160,
          }}
        >
          {String(value)}
        </Typography>
      </Tooltip>
    );
  };

  const renderDimensionCell = (column, row) => {
    const { productName, asin } = column?.innerKeys || {};
    if (productName) {
      const name = row?.[productName] ?? row?.[column.key];
      const asinValue = asin?.value ? row?.[asin.value] : null;
      const displayName = name || "-";
      const showBadge = Boolean(column?.showAdgroupStatusBadge);
      const isAdgroupEnabled = normalizeStatus(row?.adgroup_status) === "ENABLED";

      return (
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0 }}>
          {showBadge ? (
            <Box
              title={normalizeStatus(row?.adgroup_status) || "UNKNOWN"}
              sx={{
                mt: "4px",
                width: 8,
                height: 8,
                borderRadius: "50%",
                flexShrink: 0,
                bgcolor: isAdgroupEnabled ? "#12B76A" : "#98A2B3",
              }}
            />
          ) : null}
          <Box sx={{ minWidth: 0 }}>
            <Tooltip title={displayName} arrow placement="top">
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "#333",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: column.minWidth || 220,
                }}
              >
                {displayName}
              </Typography>
            </Tooltip>
            {asinValue ? (
              <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>
                {asin?.label ? `${asin.label}: ` : ""}
                {asinValue}
              </Typography>
            ) : null}
          </Box>
        </Stack>
      );
    }
    return renderStringCell(column, row);
  };

  const renderBidStrategyCell = (column, row, rowIndex) => {
    const normalizedStatus = normalizeStatus(row?.status);
    const disabled = normalizedStatus === "ARCHIVED" || isLoading;
    const value = row?.bid_strategy_api_value ?? row?.campaign_bid_strategy ?? "Down";

    return (
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <FormControl size="small" sx={{ minWidth: 110, flex: 1 }}>
          <Select
            value={value}
            disabled={disabled}
            displayEmpty
            onChange={(e) => onChangeBidStrategy?.(rowIndex, e.target.value)}
            IconComponent={disabled ? () => null : undefined}
            sx={{
              height: 32,
              fontSize: "12px",
              backgroundColor: disabled ? "#f7f7f7" : "#fff",
              "& .MuiSelect-select": {
                py: 0.5,
                px: 1,
                fontSize: "12px",
                color: "#333",
                display: "flex",
                alignItems: "center",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: disabled ? "#e0e0e0" : "#c4c4c4",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: disabled ? "#e0e0e0" : "#999",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#5864FB",
                borderWidth: 1,
              },
            }}
            MenuProps={{
              disableScrollLock: true,
              PaperProps: {
                sx: { maxHeight: 220 },
              },
            }}
          >
            <MenuItem value="Down" sx={{ fontSize: "12px" }}>
              Down
            </MenuItem>
            <MenuItem value="Up & Down" sx={{ fontSize: "12px" }}>
              Up & Down
            </MenuItem>
          </Select>
        </FormControl>
        {!disabled && (
          <EditOutlinedIcon sx={{ fontSize: 16, color: "#666", flexShrink: 0 }} />
        )}
      </Stack>
    );
  };

  const renderCell = (column, row, rowIndex) => {
    if (column.key === "status") {
      const normalizedStatus = normalizeStatus(row?.campaign_status);
      const checked = normalizedStatus === "ENABLED";
      const disabled = normalizedStatus === "ARCHIVED";
      return (
        <div
          className="child-switch-btn-container"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <SwitchBtn
            checked={checked}
            disabled={disabled}
            onChange={(e) => {
              e.stopPropagation();
              onToggleStatus?.(row);
            }}
          />
        </div>
      );
    }

    if (column.key === "campaign_bid_strategy") {
      return renderBidStrategyCell(column, row, rowIndex);
    }

    if (column?.innerKeys) return renderDimensionCell(column, row);
    if (column.type === "number") {
      const value = row?.[column.key];
      if (value === undefined || value === null || value === "") return "-";
      return numberFormatter(
        value,
        !!column?.isCurrencyFormat,
        column?.format,
        column?.maximumFractionDigits
      );
    }
    return renderStringCell(column, row);
  };

  const renderSummaryValue = (column) => {
    if (!column || column.type !== "number") return "-";
    const value =
      summaryData?.[column.key] ?? summaryData?.current_data?.[column.key];
    if (value === undefined || value === null || value === "") return "-";
    return numberFormatter(
      value,
      !!column?.isCurrencyFormat,
      column?.format,
      column?.maximumFractionDigits
    );
  };

  return (
    <StickyInsightsTable
      columns={columns}
      rows={rows}
      isLoading={isLoading}
      queryOrder={queryOrder}
      summaryData={summaryData}
      onSortClick={onSortClick}
      onToggleRow={onToggleRow}
      onToggleAll={onToggleAll}
      noDataFoundMessage={noDataFoundMessage}
      isColumnSortable={isColumnSortable}
      getAlign={getAlign}
      getRowKey={(row, index) =>
        `${row?.campaign_id}-${row?.ad_group_id}-${index}`
      }
      showSummaryLabel={false}
      renderCell={renderCell}
      renderSummaryValue={renderSummaryValue}
    />
  );
}
